import { useCallback, useMemo, useState } from 'react';

/**
 * useImageFallback
 *
 * Owns the "advance to the next photo candidate on load failure" behaviour for a
 * single rendered image. Pairs with the pure resolveListingPhotoCandidates resolver:
 * the resolver decides the ORDER, this hook drives the SWAP and the session memo.
 *
 * Keeping the memo here (not in the resolver) is what lets the resolver stay pure.
 *
 * Candidate shape (built by FallbackImage):
 *   { source: any, url?: string|null, memoize?: boolean }
 *     - source:   value handed to the image primitive's `source` prop ({ uri } or a local asset)
 *     - url:      the string key used for the dead-URL memo (null for local assets)
 *     - memoize:  true ONLY for dealer URLs. Model-image and placeholder candidates are
 *                 never memoized, because a model image is shared across every listing of a
 *                 model and a transient failure must not blacklist it for the whole session.
 *
 * See spec: .claude/specs/broken-photo-model-fallback/ (design Component 2).
 */

// Module-scoped, session-lifetime memo of dealer URLs that have already failed to load.
// In-memory only: nothing is persisted, and a full reload clears it (so a newly generated
// model image is still picked up on a later session, per Requirement 1 AC7).
const failedDealerUrls = new Set();

/** Test-only: clear the session memo between tests. Not used in app code. */
export const _resetFailedDealerUrls = () => failedDealerUrls.clear();

/** Test-only: read the session memo. Not used in app code. */
export const _getFailedDealerUrls = () => new Set(failedDealerUrls);

const firstLiveIndex = (candidates) => {
  for (let i = 0; i < candidates.length; i += 1) {
    const c = candidates[i];
    if (c && c.memoize && c.url && failedDealerUrls.has(c.url)) {
      continue; // skip a candidate we already know is dead this session
    }
    return i;
  }
  return Math.max(0, candidates.length - 1);
};

export const useImageFallback = (candidates = []) => {
  // Content key: identity-independent so a fresh inline array does not falsely "change".
  const candidatesKey = useMemo(
    () => candidates.map((c) => (c && c.url) || '·').join('|'),
    [candidates]
  );

  const startIndex = useMemo(
    () => firstLiveIndex(candidates),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [candidatesKey]
  );

  const [state, setState] = useState({ key: candidatesKey, index: startIndex, swap: 0 });

  let { index, swap } = state;
  if (state.key !== candidatesKey) {
    // Candidates changed (new listing, or model image regenerated): reset to a fresh start.
    index = startIndex;
    swap = 0;
    setState({ key: candidatesKey, index, swap });
  }

  const safeIndex = Math.min(index, Math.max(0, candidates.length - 1));
  const current = candidates[safeIndex] || null;

  const onError = useCallback(() => {
    setState((prev) => {
      const failed = candidates[prev.index];
      if (failed && failed.memoize && failed.url) {
        failedDealerUrls.add(failed.url); // dealer URLs only
      }
      const nextIndex = prev.index < candidates.length - 1 ? prev.index + 1 : prev.index;
      return { ...prev, index: nextIndex, swap: prev.swap + 1 };
    });
  }, [candidates]);

  // A changed recyclingKey forces expo-image to actually reload the swapped source
  // (rather than show a recycled/cached frame).
  const recyclingKey = current && current.url
    ? `${current.url}#${swap}`
    : `placeholder#${safeIndex}#${swap}`;

  return {
    source: current ? current.source : undefined,
    onError,
    recyclingKey,
    isExhausted: safeIndex >= candidates.length - 1,
  };
};

export default useImageFallback;
